import { ScanResult, ScanSummary, createScanSummary } from './types';
import { supabase } from '@/integrations/supabase/client';

export async function runCloudLocalSyncScan(): Promise<ScanSummary> {
  console.group('☁️ Cloud vs Local Sync Scan');
  
  const results: ScanResult[] = [];

  try {
    // Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      results.push({
        name: 'Authentication Status',
        status: 'info',
        message: 'No authenticated user - cannot compare cloud vs local data',
        details: 'Cloud sync scan requires authentication'
      });

      const summary = createScanSummary('Cloud vs Local Sync Scan', results);
      console.groupEnd();
      return summary;
    }

    results.push({
      name: 'Authentication Status',
      status: 'pass',
      message: `User authenticated: ${user.email}`,
      details: `User ID: ${user.id}`
    });

    // Check 1: Compare stats
    const { data: cloudStats, error: statsError } = await supabase
      .from('game_stats')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (statsError) {
      results.push({
        name: 'Cloud Stats Check',
        status: 'warn',
        message: 'Could not fetch cloud stats',
        details: statsError.message
      });
    } else if (cloudStats) {
      // Get local stats
      const localStatsStr = localStorage.getItem('wordpop-stats');
      const localStats = localStatsStr ? JSON.parse(localStatsStr) : null;

      if (localStats) {
        const mismatch: string[] = [];

        if (localStats.totalPlayed !== cloudStats.total_played) {
          mismatch.push(`Total played: local=${localStats.totalPlayed}, cloud=${cloudStats.total_played}`);
        }
        if (localStats.totalWon !== cloudStats.total_won) {
          mismatch.push(`Total won: local=${localStats.totalWon}, cloud=${cloudStats.total_won}`);
        }
        if (localStats.currentStreak !== cloudStats.current_streak) {
          mismatch.push(`Current streak: local=${localStats.currentStreak}, cloud=${cloudStats.current_streak}`);
        }
        if (localStats.maxStreak !== cloudStats.max_streak) {
          mismatch.push(`Max streak: local=${localStats.maxStreak}, cloud=${cloudStats.max_streak}`);
        }

        if (mismatch.length > 0) {
          // Auto-reconcile by calling recalculate_user_stats
          try {
            const { error: recalcError } = await supabase.rpc('recalculate_user_stats', { p_user_id: user.id });
            
            if (recalcError) {
              results.push({
                name: 'Stats Sync Check',
                status: 'warn',
                message: `Found ${mismatch.length} mismatch(es), auto-reconcile failed`,
                details: `${mismatch.join('; ')}. Reconcile error: ${recalcError.message}`
              });
            } else {
              results.push({
                name: 'Stats Sync Check',
                status: 'pass',
                message: `Found ${mismatch.length} mismatch(es) - auto-reconciled successfully`,
                details: `Reconciled: ${mismatch.join('; ')}. Cloud stats recalculated from game_progress.`
              });
            }
          } catch (reconcileErr) {
            results.push({
              name: 'Stats Sync Check',
              status: 'warn',
              message: `Found ${mismatch.length} mismatch(es), reconciliation error`,
              details: mismatch.join('; ')
            });
          }
        } else {
          results.push({
            name: 'Stats Sync Check',
            status: 'pass',
            message: 'Local and cloud stats match perfectly',
            details: 'All stats synchronized'
          });
        }
      } else {
        results.push({
          name: 'Local Stats Check',
          status: 'info',
          message: 'No local stats found',
          details: 'User may be using cloud-only storage'
        });
      }
    }

    // Check 2: Compare progress entries
    const { data: cloudProgress, error: progressError } = await supabase
      .from('game_progress')
      .select('puzzle_date')
      .eq('user_id', user.id);

    if (progressError) {
      results.push({
        name: 'Cloud Progress Check',
        status: 'warn',
        message: 'Could not fetch cloud progress',
        details: progressError.message
      });
    } else {
      const cloudDates = new Set(cloudProgress?.map(p => p.puzzle_date) || []);
      
      results.push({
        name: 'Cloud Progress Count',
        status: 'info',
        message: `Found ${cloudDates.size} puzzle(s) in cloud`,
        details: 'Cloud progress entries detected'
      });

      // Check for orphaned local progress
      const localProgressKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('wordpop-progress-')
      );

      const orphanedLocal: string[] = [];
      localProgressKeys.forEach(key => {
        const date = key.replace('wordpop-progress-', '');
        if (!cloudDates.has(date)) {
          orphanedLocal.push(date);
        }
      });

      results.push({
        name: 'Orphaned Local Progress',
        status: orphanedLocal.length === 0 ? 'pass' : 'info',
        message: orphanedLocal.length === 0
          ? 'No orphaned local progress entries'
          : `Found ${orphanedLocal.length} local-only progress entry(ies)`,
        details: orphanedLocal.length > 0 
          ? `Dates: ${orphanedLocal.join(', ')}` 
          : 'All local progress synced to cloud'
      });
    }

  } catch (error) {
    results.push({
      name: 'Cloud Sync Scan Error',
      status: 'fail',
      message: `Failed to run cloud sync scan: ${error}`,
      details: String(error)
    });
  }

  const summary = createScanSummary('Cloud vs Local Sync Scan', results);
  
  console.log(`✅ Passed: ${summary.passCount}`);
  console.log(`⚠️  Warnings: ${summary.warnCount}`);
  console.log(`ℹ️  Info: ${summary.infoCount}`);
  console.table(results);
  console.groupEnd();
  
  return summary;
}
